/**
 * Banking section: tabbed container for Bank Balances, Bank Transactions,
 * and Reconciliation.
 *
 * Active tab is determined by the URL path segment:
 *   /banking/balances, /banking/transactions, /banking/reconciliation
 *   (or /personal/banking/balances etc. for the personal scope)
 *
 * Shared filter state (entity, drill-through account/transaction) is
 * carried in URL query params via BankingFilterContext so deep links
 * reproduce the full filter state and tab switches do not reset filters.
 *
 * The reconciliation tab badge shows the unmatched-transaction count from
 * the matching API, cached briefly so tab-switching does not hammer it.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Landmark, Building2, ArrowLeftRight, Settings2 } from 'lucide-react';
import { DashboardLayout } from '../components/DashboardLayout';
import { PersonalLayout } from '../components/PersonalLayout';
import { Badge } from '../components/ui/badge';
import { BankBalancesPageContent } from './BankBalancesPage';
import { PersonalBankBalancesPageContent } from './PersonalBankBalancesPage';
import { TransactionsPageContent } from './TransactionsPage';
import { ReconciliationPageContent } from './ReconciliationPage';
import { BankingFilterProvider } from '../contexts/BankingFilterContext';
import { CategorizationRulesPageContent } from './CategorizationRulesPage';
import { useEntityStore } from '../store';
import { matchingAPI } from '../lib/api';
import { cn } from '../lib/utils';

var TAB_DEFS = [
  { key: 'balances', label: 'Balances', icon: Landmark, pathSuffix: '/balances' },
  { key: 'transactions', label: 'Transactions', icon: Building2, pathSuffix: '/transactions' },
  { key: 'reconciliation', label: 'Reconciliation', icon: ArrowLeftRight, pathSuffix: '/reconciliation' },
  { key: 'rules', label: 'Rules', icon: Settings2, pathSuffix: '/rules' },
];

/** Cache duration for the reconciliation badge count (ms). */
var BADGE_CACHE_MS = 60 * 1000;

function BankingTabs({ activeTab, onTabChange, unmatchedCount }) {
  return (
    <div
      className="inline-flex h-9 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground"
      data-testid="banking-tabs"
    >
      {TAB_DEFS.map(function (tab) {
        var isActive = tab.key === activeTab;
        var Icon = tab.icon;
        return (
          <button
            key={tab.key}
            onClick={function () { onTabChange(tab.key); }}
            className={cn(
              'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
              isActive && 'bg-background text-foreground shadow'
            )}
            data-testid={'banking-tab-' + tab.key}
          >
            <Icon className="h-3.5 w-3.5" />
            {tab.label}
            {tab.key === 'reconciliation' && unmatchedCount > 0 && (
              <Badge
                variant="destructive"
                className="ml-1 h-5 min-w-[20px] px-1 text-[10px]"
                data-testid="reconciliation-badge"
              >
                {unmatchedCount > 99 ? '99+' : unmatchedCount}
              </Badge>
            )}
          </button>
        );
      })}
    </div>
  );
}

function resolveActiveTab(pathname) {
  if (pathname.endsWith('/transactions')) return 'transactions';
  if (pathname.endsWith('/reconciliation')) return 'reconciliation';
  if (pathname.endsWith('/rules')) return 'rules';
  return 'balances';
}

function buildTabPath(basePath, tabKey, search) {
  var tab = TAB_DEFS.find(function (t) { return t.key === tabKey; });
  var suffix = tab ? tab.pathSuffix : '/balances';
  return basePath + suffix + (search || '');
}

export function BankingPage({ scope }) {
  var location = useLocation();
  var navigate = useNavigate();
  var selectedScope = useEntityStore(function (s) { return s.selectedScope; });
  var selectedEntity = useEntityStore(function (s) { return s.selectedEntity; });

  var isPersonal = scope === 'personal' || selectedScope === 'personal';
  var basePath = isPersonal ? '/personal/banking' : '/banking';
  // The badge must reflect the same scope + entity as the tab content it sits
  // on, so a scoped tab and its badge never disagree.
  var badgeScope = isPersonal ? 'personal' : 'entities';
  var badgeEntity = selectedEntity && selectedEntity !== 'all' ? selectedEntity : undefined;

  var activeTab = resolveActiveTab(location.pathname);

  // -- Reconciliation badge: unmatched count, cached --
  var [unmatchedCount, setUnmatchedCount] = useState(0);
  var cacheRef = useRef({ count: 0, ts: 0 });

  var fetchUnmatchedCount = useCallback(async function () {
    var now = Date.now();
    // Cache is keyed on scope+entity so switching scope or entity re-fetches
    // rather than showing another scope's cached count.
    var key = badgeScope + ':' + (badgeEntity || 'all');
    if (cacheRef.current.key === key && now - cacheRef.current.ts < BADGE_CACHE_MS) {
      setUnmatchedCount(cacheRef.current.count);
      return;
    }
    try {
      var res = await matchingAPI.getDetails({ status: 'suggested', entity_id: badgeEntity, scope: badgeScope, page: 1, page_size: 1 });
      var count = (res.data && res.data.total != null) ? res.data.total : 0;
      cacheRef.current = { count: count, ts: now, key: key };
      setUnmatchedCount(count);
    } catch (_) {
      // Non-critical: badge stays at last known value
    }
  }, [badgeScope, badgeEntity]);

  useEffect(function () {
    fetchUnmatchedCount();
  }, [fetchUnmatchedCount]);

  var handleTabChange = function (tabKey) {
    // Preserve query params (filters) when switching tabs
    navigate(buildTabPath(basePath, tabKey, location.search));
  };

  // -- Drill-through callbacks --
  // Balances -> Transactions: click account row to see its transactions
  var handleAccountDrillDown = useCallback(function (account) {
    var params = new URLSearchParams(location.search);
    params.set('account', account.id);
    params.set('accountLabel', account.account_name || account.id);
    navigate(basePath + '/transactions?' + params.toString());
  }, [basePath, location.search, navigate]);

  // Transactions -> Reconciliation: click unreconciled tx to see it in recon
  var handleTransactionDrillDown = useCallback(function (transactionId) {
    var params = new URLSearchParams(location.search);
    params.set('transaction', transactionId);
    navigate(basePath + '/reconciliation?' + params.toString());
  }, [basePath, location.search, navigate]);

  var tabContent = null;
  if (activeTab === 'balances') {
    tabContent = isPersonal
      ? <PersonalBankBalancesPageContent onAccountClick={handleAccountDrillDown} />
      : <BankBalancesPageContent onAccountClick={handleAccountDrillDown} />;
  } else if (activeTab === 'transactions') {
    tabContent = <TransactionsPageContent onTransactionDrillDown={handleTransactionDrillDown} />;
  } else if (activeTab === 'reconciliation') {
    tabContent = <ReconciliationPageContent scope={isPersonal ? 'personal' : 'entities'} />;
  } else if (activeTab === 'rules') {
    tabContent = <CategorizationRulesPageContent />;
  }

  var inner = (
    <div className="space-y-6">
      <BankingTabs
        activeTab={activeTab}
        onTabChange={handleTabChange}
        unmatchedCount={unmatchedCount}
      />
      {tabContent}
    </div>
  );

  var Layout = isPersonal ? PersonalLayout : DashboardLayout;

  return (
    <BankingFilterProvider>
      <Layout>
        {inner}
      </Layout>
    </BankingFilterProvider>
  );
}
